//DEPLOYMENT NOTE: Update API_BASE_URL origins for production
import envConfig from '../config/envConfig';

const API_BASE_URL = envConfig.apiBaseUrl;

function clearAuthStorage() {
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');
  localStorage.removeItem('mmx_user');
  localStorage.removeItem('admin_tab');
  localStorage.removeItem('learner_tab');
}

async function apiFetch(url, options = {}) {
  const token = localStorage.getItem('authToken');
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) {
    clearAuthStorage();
    window.location.href = '/auth?mode=login';
    throw new Error('Session expired. Please log in again.');
  }
  return res;
}

export const apiService = {
  // Signup API
  signup: async (formData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMsg = data.message || data.error || 'Signup failed';
        console.error('Signup error:', errorMsg, data);
        throw new Error(errorMsg);
      }

      // Store token in localStorage
      if (data.data && data.data.token) {
        localStorage.setItem('authToken', data.data.token);
        localStorage.setItem('user', JSON.stringify(data.data));
      }

      return data;
    } catch (error) {
      // Handle network errors vs API errors
      if (error instanceof TypeError) {
        console.error('Network error - Backend may not be running:', error);
        throw new Error('Cannot connect to server. Make sure backend is running on http://localhost:5000');
      }
      console.error('Signup error:', error);
      throw new Error(error.message || 'Signup failed');
    }
  },

  // Login API
  login: async (email, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMsg = data.message || data.error || 'Login failed';
        console.error('Login error:', errorMsg, data);
        throw new Error(errorMsg);
      }

      // Store token in localStorage
      if (data.data && data.data.token) {
        localStorage.setItem('authToken', data.data.token);
        localStorage.setItem('user', JSON.stringify(data.data));
      }

      return data;
    } catch (error) {
      // Handle network errors vs API errors
      if (error instanceof TypeError) {
        console.error('Network error - Backend may not be running:', error);
        throw new Error('Cannot connect to server. Make sure backend is running on http://localhost:5000');
      }
      console.error('Login error:', error);
      throw new Error(error.message || 'Login failed');
    }
  },

  // Admin Login API
  adminLogin: async (email, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/admin-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMsg = data.message || data.error || 'Admin login failed';
        console.error('Admin login error:', errorMsg, data);
        throw new Error(errorMsg);
      }

      // Store token in localStorage
      if (data.data && data.data.token) {
        localStorage.setItem('authToken', data.data.token);
        localStorage.setItem('user', JSON.stringify(data.data));
      }

      return data;
    } catch (error) {
      // Handle network errors vs API errors
      if (error instanceof TypeError) {
        console.error('Network error - Backend may not be running:', error);
        throw new Error('Cannot connect to server. Make sure backend is running on http://localhost:5000');
      }
      console.error('Admin login error:', error);
      throw new Error(error.message || 'Admin login failed');
    }
  },

  // Verify token
  verifyToken: async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Token verification failed');
      }

      return data;
    } catch (error) {
      throw new Error(error.message || 'Token verification failed');
    }
  },

  // Logout
  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  },

  // Get stored token
  getToken: () => localStorage.getItem('authToken'),

  // Get stored user
  getUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  // Check if user is authenticated
  isAuthenticated: () => !!localStorage.getItem('authToken'),

  // Get users by role
  getUsersByRole: async (role) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/users/${role}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch users');
      }

      return data;
    } catch (error) {
      console.error('Get users error:', error);
      throw new Error(error.message || 'Failed to fetch users');
    }
  },

  // Delete user
  deleteUser: async (userId) => {
    if (!userId) {
      throw new Error('Invalid user ID');
    }

    const url = `${API_BASE_URL}/auth/users/${userId}`;
    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        data = { message: response.statusText || 'Failed to delete user' };
      }

      if (!response.ok) {
        throw new Error(data.message || `Failed to delete user (${response.status})`);
      }

      return data;
    } catch (error) {
      console.error('Delete user error:', error, { url, userId });
      throw new Error(error.message || 'Failed to delete user');
    }
  },

  // Change user password
  changeUserPassword: async (userId, newPassword) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/users/${userId}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to change password');
      }

      return data;
    } catch (error) {
      console.error('Change password error:', error);
      throw new Error(error.message || 'Failed to change password');
    }
  },
  // Update logged-in user's profile
  updateProfile: async ({ firstName, lastName, bio, city, mobile, profileImage, credentials, experience, languages, introLink, linkedinUrl }) => {
    const res = await fetch(`${API_BASE_URL}/auth/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      },
      body: JSON.stringify({ firstName, lastName, bio, city, mobile, profileImage, credentials, experience, languages, introLink, linkedinUrl }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to update profile');
    return data;
  },

  // ── Content API (Languages / Categories / Subcategories) ──

  getLanguages: async () => {
    const res = await fetch(`${API_BASE_URL}/content/languages`, { headers: { 'Content-Type': 'application/json' } });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch languages');
    return data;
  },

  createLanguage: async (name, code) => {
    const res = await fetch(`${API_BASE_URL}/content/languages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify({ name, code }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to create language');
    return data;
  },

  updateLanguage: async (id, updates) => {
    const res = await fetch(`${API_BASE_URL}/content/languages/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify(updates),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to update language');
    return data;
  },

  deleteLanguage: async (id) => {
    const res = await fetch(`${API_BASE_URL}/content/languages/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to delete language');
    return data;
  },

  getCategories: async () => {
    const res = await fetch(`${API_BASE_URL}/content/categories/enabled`, { headers: { 'Content-Type': 'application/json' } });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch categories');
    return data;
  },

  createCategory: async (categoryData) => {
    const res = await fetch(`${API_BASE_URL}/content/categories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify(categoryData),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to create category');
    return data;
  },

  updateCategory: async (id, updates) => {
    const res = await fetch(`${API_BASE_URL}/content/categories/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify(updates),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to update category');
    return data;
  },

  deleteCategory: async (id) => {
    const res = await fetch(`${API_BASE_URL}/content/categories/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to delete category');
    return data;
  },

  createSubcategory: async (categoryId, name) => {
    const res = await fetch(`${API_BASE_URL}/content/categories/${categoryId}/subcategories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify({ name }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to create subcategory');
    return data;
  },

  updateSubcategory: async (id, updates) => {
    const res = await fetch(`${API_BASE_URL}/content/subcategories/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify(updates),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to update subcategory');
    return data;
  },

  deleteSubcategory: async (id) => {
    const res = await fetch(`${API_BASE_URL}/content/subcategories/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to delete subcategory');
    return data;
  },

  getServices: async () => {
    const res = await fetch(`${API_BASE_URL}/content/services`, { headers: { 'Content-Type': 'application/json' } });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch services');
    return data;
  },

  createService: async (svcData) => {
    const res = await fetch(`${API_BASE_URL}/content/services`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify(svcData),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to create service');
    return data;
  },

  updateService: async (id, svcData) => {
    const res = await fetch(`${API_BASE_URL}/content/services/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify(svcData),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to update service');
    return data;
  },

  deleteService: async (id) => {
    const res = await fetch(`${API_BASE_URL}/content/services/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to delete service');
    return data;
  },

  getTags: async () => {
    const res = await fetch(`${API_BASE_URL}/content/tags`, { headers: { 'Content-Type': 'application/json' } });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch tags');
    return data;
  },

  createTag: async (name) => {
    const res = await fetch(`${API_BASE_URL}/content/tags`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify({ name }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to create tag');
    return data;
  },

  updateTag: async (id, updates) => {
    const res = await fetch(`${API_BASE_URL}/content/tags/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify(updates),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to update tag');
    return data;
  },

  deleteTag: async (id) => {
    const res = await fetch(`${API_BASE_URL}/content/tags/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to delete tag');
    return data;
  },
  // ── Experts API ──

  getExperts: async () => {
    const res = await fetch(`${API_BASE_URL}/content/experts`, {
      headers: { 'Content-Type': 'application/json' },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch experts');
    // Normalise to the shape ExpertCard expects
    return {
      ...data,
      data: data.data.map(e => ({
        ...e,
        catId: String(e.primary_category_id || ''),
        catName: e.category_name || '',
        bg: e.category_color || '#0B6E65',
        tags: e.tag_names || [],
        rate: e.min_price ? Number(e.min_price) : 0,
        rating: 0,
        sessions: 0,
      })),
    };
  },

  // ── Slots API ──

  // Normalize a slot row from DB to match frontend field names
  _normalizeSlot: (s) => ({
    ...s,
    expertId: s.expert_id,
    start: s.start_time,
    end: s.end_time,
    service: s.service_title,
    date: s.date ? String(s.date).split('T')[0] : s.date,
  }),

  getExpertSlots: async () => {
    const res = await fetch(`${API_BASE_URL}/bookings/slots/expert`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch slots');
    return { ...data, data: data.data.map(apiService._normalizeSlot) };
  },

  getAvailableSlots: async (expertId) => {
    const res = await fetch(`${API_BASE_URL}/bookings/slots/available/${expertId}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch available slots');
    return { ...data, data: data.data.map(apiService._normalizeSlot) };
  },

  createSlot: async (slotData) => {
    const res = await fetch(`${API_BASE_URL}/bookings/slots`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify(slotData),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to create slot');
    return { ...data, data: apiService._normalizeSlot(data.data) };
  },

  deleteSlot: async (id) => {
    const res = await fetch(`${API_BASE_URL}/bookings/slots/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to delete slot');
    return data;
  },

  // ── Bookings API ──

  // Normalize a booking row from DB to match frontend field names
  _normalizeBooking: (b) => ({
    ...b,
    expertId: b.expert_id,
    receiverId: b.learner_id,
    service: b.service_title,
    videoRoom: b.video_room || '',
    slot: {
      date: b.slot_date ? String(b.slot_date).split('T')[0] : '',
      start: b.slot_start || '',
      end: b.slot_end || '',
    },
  }),

  getExpertBookings: async () => {
    const res = await fetch(`${API_BASE_URL}/bookings/expert`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch bookings');
    return { ...data, data: data.data.map(apiService._normalizeBooking) };
  },

  getLearnerBookings: async () => {
    const res = await fetch(`${API_BASE_URL}/bookings/learner`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch bookings');
    return { ...data, data: data.data.map(apiService._normalizeBooking) };
  },

  createBooking: async (bookingData) => {
    const res = await fetch(`${API_BASE_URL}/bookings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify(bookingData),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to create booking');
    return { ...data, data: apiService._normalizeBooking(data.data) };
  },

  acceptBooking: async (id) => {
    const res = await fetch(`${API_BASE_URL}/bookings/${id}/accept`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to accept booking');
    return { ...data, data: apiService._normalizeBooking(data.data) };
  },

  declineBooking: async (id) => {
    const res = await fetch(`${API_BASE_URL}/bookings/${id}/decline`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to decline booking');
    return { ...data, data: apiService._normalizeBooking(data.data) };
  },

  // ── Payments API ──

  createPaymentOrder: async (bookingData) => {
    const res = await fetch(`${API_BASE_URL}/payments/create-order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify(bookingData),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to create payment order');
    return data;
  },

  verifyPayment: async (verifyData) => {
    const res = await fetch(`${API_BASE_URL}/payments/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify(verifyData),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Payment verification failed');
    return { ...data, data: data.data ? apiService._normalizeBooking(data.data) : null };
  },

  getPaymentHistory: async () => {
    const res = await fetch(`${API_BASE_URL}/payments/history`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch payment history');
    return data;
  },

  getWallet: async () => {
    const res = await fetch(`${API_BASE_URL}/payments/wallet`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch wallet');
    return data;
  },

  requestPayout: async (payoutData) => {
    const res = await fetch(`${API_BASE_URL}/payments/payout-request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify(payoutData),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to submit payout request');
    return data;
  },

  getPayouts: async () => {
    const res = await fetch(`${API_BASE_URL}/payments/payouts`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch payouts');
    return data;
  },

  completeBooking: async (bookingId) => {
    const res = await fetch(`${API_BASE_URL}/payments/bookings/${bookingId}/complete`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to complete booking');
    return { ...data, data: data.data ? apiService._normalizeBooking(data.data) : null };
  },

  resetPassword: async (email, newPassword) => {
    const res = await fetch(`${API_BASE_URL}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, newPassword }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to reset password');
    return data;
  },

  getSecurityQuestion: async (email) => {
    const res = await fetch(`${API_BASE_URL}/auth/security-question?email=${encodeURIComponent(email)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to get security question');
    return data;
  },

  verifySecurityAnswer: async (email, answer) => {
    const res = await fetch(`${API_BASE_URL}/auth/verify-security-answer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, answer: answer.trim().toLowerCase() }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Answer verification failed');
    return data;
  },

  updateSecurityQuestion: async (email, securityQuestion, securityAnswer) => {
    const res = await fetch(`${API_BASE_URL}/auth/update-security-question`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      },
      body: JSON.stringify({ email, securityQuestion, securityAnswer }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to update security question');
    return data;
  },

  // ── Courses ──────────────────────────────────────────────────────
  createCourse: async (payload) => {
    const res = await fetch(`${API_BASE_URL}/courses/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to create course');
    return data;
  },

  getExpertCourses: async () => {
    const res = await fetch(`${API_BASE_URL}/courses/expert`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch courses');
    return data;
  },

  getCoursesForExpert: async (expertId) => {
    const res = await fetch(`${API_BASE_URL}/courses/for-expert/${expertId}`);
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch courses');
    return data;
  },

  deleteCourse: async (courseId) => {
    const res = await fetch(`${API_BASE_URL}/courses/${courseId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to delete course');
    return data;
  },

  enrollCourse: async (courseId) => {
    const res = await fetch(`${API_BASE_URL}/courses/${courseId}/enroll`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Enrollment failed');
    return data;
  },

  getMyCourseEnrollments: async () => {
    const res = await fetch(`${API_BASE_URL}/courses/my-enrollments`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch enrollments');
    return data;
  },

  // ── Session Packs ──────────────────────────────────────────
  createPack: async (payload) => {
    const res = await fetch(`${API_BASE_URL}/packs/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to create pack');
    return data;
  },

  getExpertPacks: async () => {
    const res = await fetch(`${API_BASE_URL}/packs/expert`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch packs');
    return data;
  },

  updatePack: async (packId, packData) => {
    const res = await fetch(`${API_BASE_URL}/packs/${packId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify(packData),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to update pack');
    return data;
  },

  deletePack: async (packId) => {
    const res = await fetch(`${API_BASE_URL}/packs/${packId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to delete pack');
    return data;
  },

  getPacksForExpert: async (expertId) => {
    const res = await fetch(`${API_BASE_URL}/packs/for-expert/${expertId}`);
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch packs');
    return data;
  },

  buyPack: async (packId) => {
    const res = await fetch(`${API_BASE_URL}/packs/${packId}/buy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to purchase pack');
    return data;
  },

  getMyPacks: async () => {
    const res = await fetch(`${API_BASE_URL}/packs/my-packs`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch packs');
    return data;
  },

  getActivePackForExpert: async (expertId) => {
    const res = await fetch(`${API_BASE_URL}/packs/active-for-expert/${expertId}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to check pack');
    return data;
  },

  getExpertPackEarnings: async () => {
    const res = await fetch(`${API_BASE_URL}/packs/expert-earnings`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch pack earnings');
    return data;
  },

  bookWithPackCredit: async (slotId, message) => {
    const res = await fetch(`${API_BASE_URL}/packs/book-with-credit/${slotId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify({ message }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to book with pack credit');
    return { ...data, data: data.data ? apiService._normalizeBooking(data.data) : null };
  },

  submitReview: async (bookingId, rating, comment) => {
    const res = await fetch(`${API_BASE_URL}/reviews`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify({ bookingId, rating, comment }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to submit review');
    return data;
  },

  getBookingReview: async (bookingId) => {
    const res = await fetch(`${API_BASE_URL}/reviews/booking/${bookingId}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch review');
    return data;
  },

  getExpertReviews: async (expertId) => {
    const res = await fetch(`${API_BASE_URL}/reviews/expert/${expertId}`);
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch reviews');
    return data;
  },

  createPackOrder: async (packId) => {
    const res = await fetch(`${API_BASE_URL}/payments/create-pack-order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify({ pack_id: packId }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to create pack payment order');
    return data;
  },

  verifyPackPayment: async ({ orderId }) => {
    const res = await fetch(`${API_BASE_URL}/payments/verify-pack`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify({ orderId }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Pack payment verification failed');
    return data;
  },

  getAdminExpertEarnings: async () => {
    const res = await fetch(`${API_BASE_URL}/payments/admin/expert-earnings`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch earnings');
    return data;
  },

  getJaaSToken: async (roomName) => {
    const res = await fetch(`${API_BASE_URL}/meetings/jaas-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      },
      body: JSON.stringify({ roomName }),
    });
    return res.json();
  },

  getLiveKitToken: async (roomName) => {
    const res = await fetch(`${API_BASE_URL}/meetings/livekit-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      },
      body: JSON.stringify({ roomName }),
    });
    return res.json();
  },

  getAdminAllBookings: async () => {
    const res = await fetch(`${API_BASE_URL}/bookings/admin/all`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to fetch bookings');
    return { ...data, data: data.data.map(apiService._normalizeBooking) };
  },

  getAdminStats: async () => {
    const res = await fetch(`${API_BASE_URL}/auth/admin/stats`);
    return res.json();
  },

  toggleUserStatus: async (userId, status) => {
    const res = await fetch(`${API_BASE_URL}/auth/users/${userId}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    return res.json();
  },

  // ── Requirements API ──
  postRequirement: async (data) => {
    const res = await fetch(`${API_BASE_URL}/requirements`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify(data),
    });
    const d = await res.json();
    if (!res.ok) throw new Error(d.message || 'Failed to post requirement');
    return d;
  },

  getMyRequirements: async () => {
    const res = await fetch(`${API_BASE_URL}/requirements/my`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const d = await res.json();
    if (!res.ok) throw new Error(d.message || 'Failed to fetch requirements');
    return d;
  },

  getAllRequirements: async () => {
    const res = await fetch(`${API_BASE_URL}/requirements`);
    const d = await res.json();
    if (!res.ok) throw new Error(d.message || 'Failed to fetch requirements');
    return d;
  },

  closeRequirement: async (id) => {
    const res = await fetch(`${API_BASE_URL}/requirements/${id}/close`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const d = await res.json();
    if (!res.ok) throw new Error(d.message || 'Failed to close requirement');
    return d;
  },

  deleteRequirement: async (id) => {
    const res = await fetch(`${API_BASE_URL}/requirements/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const d = await res.json();
    if (!res.ok) throw new Error(d.message || 'Failed to delete requirement');
    return d;
  },

  updateExpertCommission: async (expertId, commissionRate) => {
    const res = await fetch(`${API_BASE_URL}/auth/users/${expertId}/commission`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      },
      body: JSON.stringify({ commission_rate: commissionRate }),
    });
    const d = await res.json();
    if (!res.ok) throw new Error(d.message || 'Failed to update commission rate');
    return d;
  },

  getAdminRequirements: async () => {
    const res = await fetch(`${API_BASE_URL}/requirements/admin/all`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const d = await res.json();
    if (!res.ok) throw new Error(d.message || 'Failed to fetch requirements');
    return d;
  },

  adminCloseRequirement: async (id) => {
    const res = await fetch(`${API_BASE_URL}/requirements/${id}/close`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const d = await res.json();
    if (!res.ok) throw new Error(d.message || 'Failed to close requirement');
    return d;
  },

  adminDeleteRequirement: async (id) => {
    const res = await fetch(`${API_BASE_URL}/requirements/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
    });
    const d = await res.json();
    if (!res.ok) throw new Error(d.message || 'Failed to delete requirement');
    return d;
  },
};

export default apiService;
